import React, { useState } from 'react';
import { Label } from '../types/board';
import OwnerManagement from './OwnerManagement';
import './Sidebar.css';

interface SidebarProps {
  swimlaneMode: 'site' | 'owner' | 'date';
  labels: { [key: string]: Label };
  hiddenLabelIds: string[];
  onToggleLabel: (labelId: string) => void;
  cardCountsByLabel: { [key: string]: number };
  owners: string[];
  selectedOwner: string | null;
  onSelectOwner: (owner: string | null) => void;
  cardCountsByOwner: { [key: string]: number };
  selectedSite: string | null;
  onSelectSite: (site: string | null) => void;
  selectedDateRange: string | null;
  onSelectDateRange: (dateRange: string | null) => void;
  onOwnersChanged: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({
  swimlaneMode,
  labels,
  hiddenLabelIds,
  onToggleLabel,
  cardCountsByLabel,
  owners,
  selectedOwner,
  onSelectOwner,
  cardCountsByOwner,
  selectedSite,
  onSelectSite,
  selectedDateRange,
  onSelectDateRange,
  onOwnersChanged,
}) => {
  const [showOwnerManagement, setShowOwnerManagement] = useState(false);

  const dateRanges = [
    { id: 'date-overdue', name: 'Overdue', icon: '🔴' },
    { id: 'date-today', name: 'Today', icon: '🟡' },
    { id: 'date-thisweek', name: 'This Week', icon: '🔵' },
    { id: 'date-later', name: 'Later', icon: '🟢' },
    { id: 'date-nodate', name: 'No Date', icon: '⚫' },
  ];

  return (
    <div className="sidebar">
      {/* Owner Management Button */}
      <div className="sidebar-section sidebar-owner-management">
        <button
          className="sidebar-manage-owners-btn"
          onClick={() => setShowOwnerManagement(true)}
        >
          <span className="manage-owners-icon">👥</span>
          <span className="manage-owners-text">Manage Owners</span>
        </button>
      </div>

      {/* Site filter - show when NOT in site mode */}
      {swimlaneMode !== 'site' && (
        <div className="sidebar-section">
          <div className="sidebar-header">
            <h3>Filter by Site</h3>
          </div>
          <div className="sidebar-content">
            <button
              className={`filter-item ${selectedSite === null ? 'active' : ''}`}
              onClick={() => onSelectSite(null)}
            >
              <span className="filter-icon">🏢</span>
              <span className="filter-name">All Sites</span>
            </button>
            {Object.values(labels).map((label) => (
              <button
                key={label.id}
                className={`filter-item ${selectedSite === label.id ? 'active' : ''}`}
                onClick={() => onSelectSite(label.id)}
              >
                <span
                  className="label-dot"
                  style={{ backgroundColor: label.color }}
                ></span>
                <span className="filter-name">{label.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Owner filter - show when NOT in owner mode */}
      {swimlaneMode !== 'owner' && (
        <div className="sidebar-section">
          <div className="sidebar-header">
            <h3>Filter by Owner</h3>
          </div>
          <div className="sidebar-content">
            <button
              className={`filter-item ${selectedOwner === null ? 'active' : ''}`}
              onClick={() => onSelectOwner(null)}
            >
              <span className="filter-icon">👥</span>
              <span className="filter-name">All Owners</span>
            </button>
            {owners.map((owner) => (
              <button
                key={owner}
                className={`filter-item ${selectedOwner === owner ? 'active' : ''}`}
                onClick={() => onSelectOwner(owner)}
              >
                <span className="filter-icon">👤</span>
                <span className="filter-name">{owner}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Date filter - show when NOT in date mode */}
      {swimlaneMode !== 'date' && (
        <div className="sidebar-section">
          <div className="sidebar-header">
            <h3>Filter by Due Date</h3>
          </div>
          <div className="sidebar-content">
            <button
              className={`filter-item ${selectedDateRange === null ? 'active' : ''}`}
              onClick={() => onSelectDateRange(null)}
            >
              <span className="filter-icon">📅</span>
              <span className="filter-name">All Dates</span>
            </button>
            {dateRanges.map((range) => (
              <button
                key={range.id}
                className={`filter-item ${selectedDateRange === range.id ? 'active' : ''}`}
                onClick={() => onSelectDateRange(range.id)}
              >
                <span className="filter-icon">{range.icon}</span>
                <span className="filter-name">{range.name}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Owner Management Modal */}
      {showOwnerManagement && (
        <OwnerManagement
          onClose={() => setShowOwnerManagement(false)}
          onOwnersChanged={onOwnersChanged}
        />
      )}
    </div>
  );
};

export default Sidebar;
